import styled from "styled-components";
import type { EligibleWorkPolicy } from "../../../../api/work-policy-application-api.types";

export type EligiblePolicyStatus =
  | "loading"
  | "ready"
  | "error"
  | "forbidden";

interface EligiblePolicySelectProps {
  status: EligiblePolicyStatus;
  policies: EligibleWorkPolicy[];
  selectedPolicyId: number | null;
  disabled?: boolean;
  onSelect: (policy: EligibleWorkPolicy) => void;
  onRetry: () => void;
}

const SelectStack = styled.div`
  display: grid;
  min-width: 0;
  width: 100%;
  gap: 6px;

  select {
    width: 100%;
    min-width: 0;
    min-height: 34px;
    border: 1px solid ${props => props.theme.colors.darkGrey};
    background: ${props => props.theme.colors.white};
  }
`;

const State = styled.div`
  width: 100%;
  min-height: 32px;
  padding: 8px 10px;
  border: 1px solid ${props => props.theme.colors.grey};
  background: ${props => props.theme.colors.white};
`;

function policyIdentity(policy: EligibleWorkPolicy) {
  if (policy.workType === "staggered") return "시차 출퇴근";
  if (policy.workType === "selective") return "선택근무";
  return policy.flexibleMode === "DAILY_DATE"
    ? "탄력근무 날짜별"
    : "탄력근무 주간";
}

function policySummary(policy: EligibleWorkPolicy) {
  return `${policyIdentity(policy)} · 일 ${policy.dailyBaseHours}시간 · 주 ${
    policy.weeklyBaseHours
  }시간 · 연장 한도 ${policy.weeklyOvertimeLimit}시간`;
}

export function EligiblePolicySelect({
  status,
  policies,
  selectedPolicyId,
  disabled = false,
  onSelect,
  onRetry
}: EligiblePolicySelectProps) {
  if (status === "loading") {
    return <State role="status">신청 가능한 정책을 불러오는 중입니다.</State>;
  }
  if (status === "forbidden") {
    return (
      <State role="alert">
        신청 권한이 없어 근무제 목록을 볼 수 없습니다. 안전하게 이전 화면으로
        돌아가 주세요.
      </State>
    );
  }
  if (status === "error") {
    return (
      <State role="alert">
        정책을 불러오지 못했습니다. <button onClick={onRetry}>다시 시도</button>
      </State>
    );
  }
  if (policies.length === 0) {
    return (
      <State>
        신청 가능한 근무제가 없습니다. 관리자에게 정책 대상과 노출 상태를
        확인해 주세요.
      </State>
    );
  }
  const selected = policies.find(
    policy => policy.workPolicyId === selectedPolicyId
  );
  return (
    <SelectStack>
      <select
        id="work-policy-id"
        aria-label="근무제 유형"
        aria-describedby={!selected ? "work-policy-selection-guide" : undefined}
        disabled={disabled}
        value={selectedPolicyId ?? ""}
        onChange={event => {
          const policy = policies.find(
            item => item.workPolicyId === Number(event.currentTarget.value)
          );
          if (policy) onSelect(policy);
        }}
      >
        <option value="">신청할 근무제 정책을 선택해 주세요</option>
        {policies.map(policy => (
          <option key={policy.workPolicyId} value={policy.workPolicyId}>
            {policy.profileName}
          </option>
        ))}
      </select>
      {selected ? (
        <p>{policySummary(selected)}</p>
      ) : (
        <p id="work-policy-selection-guide">
          정책을 선택하면 제목 아래에 해당 신청 입력 화면이 표시됩니다.
        </p>
      )}
    </SelectStack>
  );
}
